/**
 * Smoother surfaces for the tongue: one step of Loop subdivision on the rest mesh (each triangle
 * into four, the new and old points moved by Loop's weights, so facets round off instead of just
 * splitting), and Taubin smoothing of a posed mesh (a shrink step and an inflate step, so creases
 * from bending smooth out without the volume shrinking).
 */

export interface Mesh {
  vertices: Float32Array;
  faces: Uint32Array;
}

/** Each vertex's neighbours, as offsets into a flat list (CSR). */
export interface Neighbours {
  start: Uint32Array;
  list: Uint32Array;
}

export function neighbours(vertexCount: number, faces: ArrayLike<number>): Neighbours {
  const sets: Set<number>[] = Array.from({ length: vertexCount }, () => new Set<number>());
  for (let f = 0; f < faces.length; f += 3) {
    const a = faces[f] ?? 0;
    const b = faces[f + 1] ?? 0;
    const c = faces[f + 2] ?? 0;
    sets[a]?.add(b).add(c);
    sets[b]?.add(a).add(c);
    sets[c]?.add(a).add(b);
  }
  const start = new Uint32Array(vertexCount + 1);
  for (let v = 0; v < vertexCount; v++) start[v + 1] = (start[v] ?? 0) + (sets[v]?.size ?? 0);
  const list = new Uint32Array(start[vertexCount] ?? 0);
  for (let v = 0; v < vertexCount; v++) {
    let i = start[v] ?? 0;
    for (const n of sets[v] ?? []) list[i++] = n;
  }
  return { start, list };
}

/**
 * One step of Loop subdivision as fixed weights (each new point a weighted sum of old points),
 * so a mesh that keeps its connectivity can be subdivided again each frame by `applyStencil`.
 * Boundary edges and points follow the curve-only rule.
 */
export interface Stencil {
  faces: Uint32Array;
  /** Per new point, its terms' range in `index` and `weight`. */
  start: Uint32Array;
  index: Uint32Array;
  weight: Float32Array;
}

export function loopStencil(vertexCount: number, F: Uint32Array): Stencil {
  const n = vertexCount;
  const key = (a: number, b: number) => (a < b ? a * n + b : b * n + a);
  const edges = new Map<number, { a: number; b: number; opp: number[]; id: number }>();
  for (let f = 0; f < F.length; f += 3) {
    for (let k = 0; k < 3; k++) {
      const a = F[f + k] ?? 0;
      const b = F[f + ((k + 1) % 3)] ?? 0;
      const c = F[f + ((k + 2) % 3)] ?? 0;
      const id = key(a, b);
      const e = edges.get(id);
      if (e) e.opp.push(c);
      else edges.set(id, { a, b, opp: [c], id: n + edges.size });
    }
  }
  const terms: [number, number][][] = Array.from({ length: n + edges.size }, () => []);
  for (const e of edges.values()) {
    const [o0, o1] = e.opp;
    terms[e.id] =
      e.opp.length === 2 && o0 !== undefined && o1 !== undefined
        ? [
            [e.a, 3 / 8],
            [e.b, 3 / 8],
            [o0, 1 / 8],
            [o1, 1 / 8],
          ]
        : [
            [e.a, 1 / 2],
            [e.b, 1 / 2],
          ];
  }
  const nb = neighbours(n, F);
  const boundary: number[][] = Array.from({ length: n }, () => []);
  for (const e of edges.values())
    if (e.opp.length === 1) {
      boundary[e.a]?.push(e.b);
      boundary[e.b]?.push(e.a);
    }
  for (let v = 0; v < n; v++) {
    const ring = boundary[v] ?? [];
    if (ring.length >= 2) {
      terms[v] = [
        [v, 3 / 4],
        [ring[0] ?? v, 1 / 8],
        [ring[1] ?? v, 1 / 8],
      ];
      continue;
    }
    const s = nb.start[v] ?? 0;
    const k = (nb.start[v + 1] ?? 0) - s;
    const beta =
      k > 3 ? (1 / k) * (5 / 8 - (3 / 8 + (1 / 4) * Math.cos((2 * Math.PI) / k)) ** 2) : 3 / 16;
    const t: [number, number][] = [[v, 1 - k * beta]];
    for (let i = 0; i < k; i++) t.push([nb.list[s + i] ?? v, beta]);
    terms[v] = t;
  }
  const start = new Uint32Array(terms.length + 1);
  terms.forEach((t, i) => {
    start[i + 1] = (start[i] ?? 0) + t.length;
  });
  const index = new Uint32Array(start[terms.length] ?? 0);
  const weight = new Float32Array(index.length);
  terms.forEach((t, i) => {
    t.forEach(([j, w], k) => {
      index[(start[i] ?? 0) + k] = j;
      weight[(start[i] ?? 0) + k] = w;
    });
  });
  const faces = new Uint32Array(F.length * 4);
  const mid = (a: number, b: number) => edges.get(key(a, b))?.id ?? a;
  for (let f = 0, o = 0; f < F.length; f += 3) {
    const a = F[f] ?? 0;
    const b = F[f + 1] ?? 0;
    const c = F[f + 2] ?? 0;
    const ab = mid(a, b);
    const bc = mid(b, c);
    const ca = mid(c, a);
    faces.set([a, ab, ca, ab, b, bc, ca, bc, c, ab, bc, ca], o);
    o += 12;
  }
  return { faces, start, index, weight };
}

/** The subdivided points of `positions` (xyz per old point) into `out` (xyz per new point). */
export function applyStencil(stencil: Stencil, positions: ArrayLike<number>, out: Float32Array) {
  const { start, index, weight } = stencil;
  for (let i = 0; i + 1 < start.length; i++) {
    let x = 0;
    let y = 0;
    let z = 0;
    for (let t = start[i] ?? 0; t < (start[i + 1] ?? 0); t++) {
      const j = (index[t] ?? 0) * 3;
      const w = weight[t] ?? 0;
      x += w * (positions[j] ?? 0);
      y += w * (positions[j + 1] ?? 0);
      z += w * (positions[j + 2] ?? 0);
    }
    out[i * 3] = x;
    out[i * 3 + 1] = y;
    out[i * 3 + 2] = z;
  }
}

/** One step of Loop subdivision of a mesh. */
export function loopSubdivide(mesh: Mesh): Mesh {
  const stencil = loopStencil(mesh.vertices.length / 3, mesh.faces);
  const vertices = new Float32Array((stencil.start.length - 1) * 3);
  applyStencil(stencil, mesh.vertices, vertices);
  return { vertices, faces: stencil.faces };
}

/**
 * Taubin smoothing in place: per pass, a step toward the neighbours' mean (lambda) then one away
 * from it (mu, a little stronger), which removes creases without shrinking the surface.
 */
export function taubinSmooth(
  positions: Float32Array,
  nb: Neighbours,
  passes = 1,
  lambda = 0.5,
  mu = -0.53,
  scratch = new Float32Array(positions.length),
): void {
  const n = positions.length / 3;
  for (let p = 0; p < passes; p++) {
    for (const factor of [lambda, mu]) {
      for (let v = 0; v < n; v++) {
        const s = nb.start[v] ?? 0;
        const k = (nb.start[v + 1] ?? 0) - s;
        for (let d = 0; d < 3; d++) {
          const own = positions[v * 3 + d] ?? 0;
          if (k === 0) {
            scratch[v * 3 + d] = own;
            continue;
          }
          let sum = 0;
          for (let i = 0; i < k; i++) sum += positions[(nb.list[s + i] ?? v) * 3 + d] ?? 0;
          scratch[v * 3 + d] = own + factor * (sum / k - own);
        }
      }
      positions.set(scratch);
    }
  }
}
